import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import Form, {
  Label,
  RangeRule,
  Item as FormItem,
  GroupItem as GroupForm,
  TabbedItem,
  TabPanelOptions,
  Tab,
  EmptyItem,
} from "devextreme-react/form";
import handlePutOrders from "../../../Utils/handlePUT/handlePutOrders";
import handlePostAddOrder from "../../../Utils/handlePOST/handlePostAddOrder";
import handlePostAddOrderForOther from "../../../Utils/handlePOST/handlePostAddOrderForOther";
import { isMobile } from "../../../Utils/media-query";
import useFetch from "../../../Hooks/useFetch";
import { getUsersWorkCenter } from "../../../Utils/helper";
import useGetAllUsers from "../../../Hooks/useGET/useGetAllUsers";
import useGetDeliveryLocations from "../../../Hooks/useGET/useGetDeliveryLocations";
import useGetBillingCodes from "../../../Hooks/useGET/useGetBillingCodes";
import { statusKey } from "../../../Components/OrderComponents/constants";
import "../styles.css";

// ─── Static editor options (defined outside component to avoid recreating on each render) ───
const orderDateOptions = {
  type: "datetime",
  onValueChanged: (e) => {
    e.component.options("text", formatDate(e.value));
  },
};

const urgencyKey = [
  { id: 0, name: "High" },
  { id: 1, name: "Standard" },
  { id: 2, name: "Low" },
];

const urgencyOptions = {
  items: urgencyKey,
  valueExpr: "id",
  displayExpr: "name",
};

const statusOptions = {
  readOnly: true,
  items: statusKey,
  valueExpr: "id",
  displayExpr: "name",
};

const costOptions = { format: "$#.##" };
const quantityOptions = { format: "#" };

export default function OrderForm({
  selectedOrder,
  setNotifications,
  mutate,
  onClose,
}) {
  /*-=-=-=-=-=-=-=-=-=-=-=-= Default objects -=-=-=-=-=-=-=-=-=-=-=-=*/
  const defaultItem = [
      {
        itemNumber: "",
        itemName: "",
        url: "",
        quantity: "",
        pricePerItem: "",
        deliveryLocationId: "",
        billingCode: "",
        isOutOfStock: false,
        notes: "",
      },
    ],
    defaultVendor = [
      {
        id: "",
        name: "",
        trackingNumber: "",
        items: defaultItem,
      },
    ];

  const navigate = useNavigate(),
    fetch = useFetch({ navigate }),
    // ─── Optional chaining guards against undefined workcenter during login transitions ───
    workcenter = getUsersWorkCenter()?.name ?? "",
    { allUsers } = useGetAllUsers(),
    { billingCodes } = useGetBillingCodes(),
    isNewEntry = Object.keys(selectedOrder).length === 0,
    { deliveryLocations } = useGetDeliveryLocations(),
    [isLoading, setLoading] = useState(false),
    [formVendors, setFormVendors] = useState({ vendors: [] }),
    [formData, setFormData] = useState({
      ...selectedOrder,
      reasoning: selectedOrder?.reasoning ?? "",
    }),
    [selfOrder, setSelfOrder] = useState(true),
    // ─── Optional chaining guards against undefined workcenter ───
    showOrderForOthers =
      getUsersWorkCenter()?.name === "Operations" && isNewEntry,
    [activeItemTabs, setActiveItemTabs] = useState({});

  // ─── Controls the discard confirmation popup ───
  const [confirmClose, setConfirmClose] = useState(false);

  const formRef1 = useRef(null);
  const formRef2 = useRef(null);

  const deliveryOptions = useMemo(
    () => ({
      items: deliveryLocations,
      valueExpr: "id",
      displayExpr: "location",
      searchEnabled: true,
      placeholder: "Type or Select...",
    }),
    [deliveryLocations],
  );

  const deleteVendor = useCallback(
    (vendorKey) => {
      const updatedVendors = formVendors.vendors.filter(
        (vendor) => vendor.key !== vendorKey,
      );
      setFormVendors({ vendors: updatedVendors });
    },
    [formVendors.vendors],
  );

  const deleteVendorOptions = useCallback(
    (key) => ({
      buttons: [
        {
          name: "trash",
          location: "after",
          options: {
            stylingMode: "text",
            icon: "trash",
            onClick: () => deleteVendor(key),
          },
        },
      ],
    }),
    [deleteVendor],
  );

  const userSelectBoxOptions = useMemo(
    () => ({
      items: allUsers,
      valueExpr: "value",
      displayExpr: "label",
      placeholder: "Select...",
      searchEnabled: true,
    }),
    [allUsers],
  );

  const billingCodeOptions = useMemo(
    () => ({
      items: billingCodes,
      searchEnabled: true,
      placeholder: "Type or Select...",
    }),
    [billingCodes],
  );

  // ─── Opens confirmation popup instead of closing immediately ───
  const cancelOptions = useMemo(
    () => ({
      stylingMode: "outlined",
      text: "Cancel",
      onClick: () => setConfirmClose(true),
    }),
    [],
  );

  // ─── Confirmation popup button options ───
  const confirmDiscardOptions = useMemo(
    () => ({
      text: "Discard",
      stylingMode: "contained",
      type: "danger",
      onClick: () => {
        setConfirmClose(false);
        onClose();
      },
    }),
    [onClose],
  );

  const keepEditingOptions = useMemo(
    () => ({
      text: "Keep Editing",
      stylingMode: "outlined",
      onClick: () => setConfirmClose(false),
    }),
    [],
  );

  /*-=-=-=-=-=-=-=-=-=-=-=-= Key Generation Logic -=-=-=-=-=-=-=-=-=-=-=-=*/
  // ─── Stable keys prevent tab/form items from losing state when indices shift ───
  const counterRef = useRef(0);

  const generateKey = () => {
    counterRef.current += 1;
    return `${Date.now()}-${counterRef.current}`;
  };

  const giveVendorsKeys = (vendors) => {
    const updatedVendors = vendors?.map((vendor) => {
      if (!vendor.hasOwnProperty("key")) {
        vendor.key = generateKey();
      }
      vendor.items?.forEach((item) => {
        if (!item.hasOwnProperty("key")) {
          item.key = generateKey();
        }
      });
      return vendor;
    });
    setFormVendors({ vendors: updatedVendors });
  };

  // ─── Runs once on mount to assign keys to existing or default vendors ───
  useEffect(() => {
    if (selectedOrder?.vendors?.length > 0) {
      giveVendorsKeys(selectedOrder?.vendors);
    } else {
      giveVendorsKeys(defaultVendor);
    }
  }, []);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      ...selectedOrder,
      // ─── Try common backend field name variants for urgency justification ───
      reasoning:
        selectedOrder?.reasoning ??
        selectedOrder?.urgencyJustification ??
        selectedOrder?.justification ??
        "",
    }));
  }, [selectedOrder]);

  const userCheckBoxOptions = {
    value: !selfOrder,
    text: "Ordering for someone else?",
    onValueChanged(args) {
      setSelfOrder(!selfOrder);
    },
    elementAttr: { class: "checkbox" },
  };

  const onFieldDataChanged = (e) => {
    if (e?.dataField === "vendors[0].trackingNumber") {
      if (selectedOrder?.status === 6) {
        console.log("shipped");
      }
    }
  };

  /*-=-=-=-=-=-=-=-=-=-=-=-= Rendering Logic -=-=-=-=-=-=-=-=-=-=-=-=*/
  const renderVendorName = (vendorName, index) => {
    return vendorName || `Vendor ${index + 1}`;
  };

  const renderItemsName = (itemName, index) => {
    return itemName || `Item ${index + 1}`;
  };

  const handleDataLogic = useCallback(
    (response, isDataGood) => {
      setNotifications(response);
      if (isDataGood) {
        mutate();
        onClose();
      }
    },
    [mutate, onClose, setNotifications],
  );

  /*-=-=-=-=-=-=-=-=-=-=-=-= Save / Update logic -=-=-=-=-=-=-=-=-=-=-=-=*/
  const handleUpdateSave = useCallback(
    async (keylessVendors) => {
      const vendors = keylessVendors;
      vendors.forEach(
        (x) => (x.items = Object.entries(x.items).map((y) => y[1])),
      );
      const data = {
        id: formData.id,
        requestorContactNumber: formData.requestorContactNumber,
        vendors: vendors,
        urgency: formData.urgency,
        reasoning: formData.reasoning,
      };
      const status =
        vendors[0]?.trackingNumber !== null && formData.status === 6
          ? 7
          : formData.status;
      const { response, isDataGood } = await handlePutOrders(
        data,
        status,
        fetch,
      );
      handleDataLogic(response, isDataGood);
      console.log("update body response, isDataGood ", response, isDataGood);
    },
    [fetch, formData, handleDataLogic],
  );

  const handleNewSave = useCallback(
    async (keylessVendors) => {
      const vendors = keylessVendors.map((x) => ({
        name: x.name,
        items: Object.entries(x.items).map((y) => y[1]),
      }));
      if (selfOrder) {
        const { response, isDataGood } = await handlePostAddOrder(
          formData.requestorContactNumber,
          vendors,
          formData.urgency,
          fetch,
          formData.reasoning,
        );
        handleDataLogic(response, isDataGood);
      } else {
        const { response, isDataGood } = await handlePostAddOrderForOther(
          formData.requestorContactNumber,
          vendors,
          formData.urgency,
          formData.orderedBy.name,
          fetch,
          formData.reasoning,
        );
        handleDataLogic(response, isDataGood);
      }
    },
    [fetch, formData, selfOrder, handleDataLogic],
  );

  const handleSave = useCallback(async () => {
    setLoading(true);
    try {
      let formValidation1 = formRef1.current.instance.validate();
      let formValidation2 = formRef2.current.instance.validate();

      if (formValidation1.isValid && formValidation2.isValid) {
        const keylessVendors = formVendors.vendors.map((vendor) => {
          const { key, ...keylessVendor } = vendor;
          const keylessItems = vendor.items.map((item) => {
            const { key, ...keylessItem } = item;
            return keylessItem;
          });
          return { ...keylessVendor, items: keylessItems };
        });
        console.log("Form data is valid. Submitting form...");
        if (!isNewEntry) {
          await handleUpdateSave(keylessVendors);
        } else {
          await handleNewSave(keylessVendors);
        }
      }
    } catch (error) {
      console.error("An error occurred:", error);
    } finally {
      setLoading(false);
    }
  }, [formVendors.vendors, handleNewSave, handleUpdateSave, isNewEntry]);

  const submitOptions = useMemo(
    () => ({
      stylingMode: "contained",
      useSubmitBehavior: false,
      disabled: isLoading,
      text: isNewEntry ? "Save" : "Update",
      onClick: () => handleSave(),
    }),
    [isNewEntry, isLoading, handleSave],
  );

  /*-=-=-=-=-=-=-=-=-=-=-=-= Vendor / Item add + remove logic -=-=-=-=-=-=-=-=-=-=-=-=*/
  const addVendor = () => {
    const updatedVendors = [...formVendors.vendors, ...defaultVendor];
    giveVendorsKeys(updatedVendors);
  };

  const addItemToVendor = (vendorKey) => {
    const updatedVendors = formVendors.vendors.map((vendor) => {
      if (vendor.key === vendorKey) {
        return { ...vendor, items: [...vendor.items, ...defaultItem] };
      }
      return vendor;
    });
    giveVendorsKeys(updatedVendors);
    const vendor = updatedVendors.find((v) => v.key === vendorKey);
    setActiveItemTabs((prev) => ({
      ...prev,
      [vendorKey]: vendor.items.length - 1,
    }));
  };

  const deleteItem = (vendorKey, itemKey) => {
    const updatedVendors = formVendors.vendors.map((vendor) => {
      if (vendor.key === vendorKey) {
        return {
          ...vendor,
          items: vendor.items.filter((item) => item.key !== itemKey),
        };
      }
      return vendor;
    });
    setFormVendors({ vendors: updatedVendors });
  };

  /*-=-=-=-=-=-=-=-=-=-=-=-= Order total calculation -=-=-=-=-=-=-=-=-=-=-=-=*/
  const orderTotal = useMemo(() => {
    return formVendors.vendors.reduce((vendorAcc, vendor) => {
      const vendorTotal = vendor.items.reduce((itemAcc, item) => {
        const qty = Number(item.quantity) || 0;
        const price = Number(item.pricePerItem) || 0;
        return itemAcc + qty * price;
      }, 0);
      return vendorAcc + vendorTotal;
    }, 0);
  }, [formVendors.vendors]);

  return (
    <>
      {/* ─── Discard confirmation -- shown when user clicks Cancel ─── */}
      {confirmClose && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "#2b2b2b",
              borderRadius: 8,
              padding: "24px",
              width: 340,
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            }}
          >
            <h3
              style={{ margin: "0 0 12px 0", color: "white", fontWeight: 600 }}
            >
              Discard Changes?
            </h3>
            <p style={{ margin: "0 0 24px 0", color: "#ccc", lineHeight: 1.5 }}>
              You have unsaved changes. Are you sure you want to close?
            </p>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <button
                onClick={() => setConfirmClose(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 4,
                  border: "1px solid #FFCD00",
                  backgroundColor: "transparent",
                  color: "#FFCD00",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Keep Editing
              </button>
              <button
                onClick={() => {
                  setConfirmClose(false);
                  onClose();
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: 4,
                  border: "none",
                  backgroundColor: "#c0392b",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Main order form popup ─── */}
      <Popup
        visible={true}
        resizeEnabled={true}
        title="Order Info"
        fullScreen={isMobile}
        showTitle={true}
        width="55%"
        height="90%"
      >
        <ToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          options={cancelOptions}
        />
        <ToolbarItem
          widget="dxButton"
          toolbar="bottom"
          location="after"
          options={submitOptions}
        />

        <Form
          ref={formRef1}
          formData={formData}
          onFieldDataChanged={onFieldDataChanged}
        >
          <GroupForm colCount={2} colSpan={2}>
            <FormItem dataField="id" visible={false} />

            <FormItem
              colSpan={2}
              visible={showOrderForOthers}
              editorType="dxCheckBox"
              editorOptions={userCheckBoxOptions}
            >
              <Label text={" "} />
            </FormItem>

            <FormItem
              dataField="orderedBy.name"
              disabled={!isNewEntry}
              colSpan={!isNewEntry ? 1 : 2}
              visible={!selfOrder}
              editorType="dxSelectBox"
              editorOptions={userSelectBoxOptions}
            />

            <FormItem
              dataField="orderedDate"
              disabled={true}
              colSpan={1}
              visible={!isNewEntry}
              editorType={"dxDateBox"}
              editorOptions={orderDateOptions}
            />
            <FormItem
              dataField="urgency"
              editorType="dxSelectBox"
              isRequired={true}
              colSpan={!isNewEntry ? 1 : 2}
              editorOptions={{
                ...urgencyOptions,
                onValueChanged: (e) => {
                  setFormData((prev) => ({ ...prev, urgency: e.value }));
                },
              }}
            />

            {formData.urgency === 0 && (
              <FormItem
                dataField="reasoning"
                colSpan={2}
                editorType="dxTextArea"
                isRequired={true}
                editorOptions={{
                  value: formData.reasoning,
                  onValueChanged: (e) => {
                    setFormData((prev) => ({ ...prev, reasoning: e.value }));
                  },
                }}
              >
                <Label text="Urgency Justification" />
              </FormItem>
            )}
            <FormItem
              dataField="status"
              disabled={workcenter !== "Operations"}
              colSpan={1}
              visible={!isNewEntry}
              editorType="dxSelectBox"
              editorOptions={{ ...statusOptions }}
            />
            <FormItem
              dataField="requestorContactNumber"
              colSpan={!isNewEntry ? 1 : 2}
              editorType="dxNumberBox"
            />
          </GroupForm>
          <FormItem itemType="empty" colSpan={2} />
        </Form>

        <Form ref={formRef2} formData={formVendors}>
          {formVendors?.vendors?.map((vendor, index) => (
            <GroupForm colCount={2} colSpan={2} key={vendor.key}>
              <GroupForm
                cssClass="form-headers"
                colCount={2}
                colSpan={2}
                caption={renderVendorName(null, index)}
                key={`group1_${vendor.key}`}
              >
                <FormItem
                  key={`name_${vendor.key}`}
                  colSpan={isNewEntry ? 2 : 1}
                  dataField={`vendors[${index}].name`}
                  editorOptions={deleteVendorOptions(vendor.key)}
                  isRequired={true}
                >
                  <Label text={renderVendorName(null, index)} />
                </FormItem>

                <FormItem
                  key={`trackingNum_${vendor.key}`}
                  dataField={`vendors[${index}].trackingNumber`}
                  visible={!isNewEntry}
                >
                  <Label text="Tracking Number" />
                </FormItem>
              </GroupForm>

              <FormItem
                key={`addItem_${vendor.key}`}
                colSpan={2}
                className="popup-buttons"
                itemType="button"
                horizontalAlignment="left"
                buttonOptions={{
                  icon: "add",
                  text: "Add Item",
                  onClick: () => addItemToVendor(vendor.key),
                }}
              />

              {/*-=-=-=- Tabbed Items =-=-=-=-*/}
              <TabbedItem colSpan={2}>
                <TabPanelOptions
                  showNavButtons={true}
                  selectedIndex={activeItemTabs[vendor.key] ?? 0}
                  onSelectedIndexChanged={(e) =>
                    setActiveItemTabs((prev) => ({
                      ...prev,
                      [vendor.key]: e.component.option("selectedIndex"),
                    }))
                  }
                />

                {vendor?.items?.map((item, itemIndex) => (
                  <Tab
                    title={renderItemsName(item.itemName, itemIndex)}
                    key={item.key}
                  >
                    <GroupForm
                      cssClass="form-container2"
                      key={`Group0_${item.key}`}
                      colCount={3}
                      colSpan={2}
                    >
                      <FormItem
                        disabled={vendor.items?.length <= 1}
                        className="popup-buttons-remove"
                        colSpan={2}
                        itemType="button"
                        horizontalAlignment="left"
                        key={`deleteItem_${item.key}`}
                        buttonOptions={{
                          icon: "minus",
                          text: "Remove Item",
                          onClick: () => deleteItem(vendor.key, item.key),
                        }}
                      />
                      <FormItem
                        key={`outOfOrder_${item.key}`}
                        dataField={`vendors[${index}].items[${itemIndex}].isOutOfStock`}
                        colSpan={1}
                        editorType="dxCheckBox"
                        editorOptions={{
                          text: "Out of Stock",
                          elementAttr: { class: "checkbox" },
                        }}
                      >
                        <Label text={" "} location="left" />
                      </FormItem>
                    </GroupForm>

                    <GroupForm
                      cssClass="form-container2"
                      key={`Group1_${item.key}`}
                      colCount={5}
                      colSpan={2}
                    >
                      <FormItem
                        cssClass="form-container"
                        key={`itemName_${item.key}`}
                        dataField={`vendors[${index}].items[${itemIndex}].itemName`}
                        isRequired={true}
                        colSpan={3}
                      >
                        <Label text="Item Name" />
                      </FormItem>

                      <FormItem
                        cssClass="form-container"
                        key={`itemNumber_${item.key}`}
                        dataField={`vendors[${index}].items[${itemIndex}].itemNumber`}
                        isRequired={true}
                        colSpan={2}
                      >
                        <Label text="Item Number" />
                      </FormItem>
                    </GroupForm>

                    <GroupForm
                      cssClass="form-container2"
                      key={`Group2_${item.key}`}
                      colCount={2}
                      colSpan={2}
                    >
                      <FormItem
                        key={`delivery_${item.key}`}
                        dataField={`vendors[${index}].items[${itemIndex}].deliveryLocationId`}
                        isRequired={true}
                        colSpan={1}
                        editorType="dxSelectBox"
                        editorOptions={deliveryOptions}
                      >
                        <Label text="Delivery Location" />
                      </FormItem>

                      <FormItem
                        key={`billingCodes_${item.key}`}
                        dataField={`vendors[${index}].items[${itemIndex}].billingCode`}
                        isRequired={true}
                        colSpan={1}
                        editorType="dxSelectBox"
                        editorOptions={billingCodeOptions}
                      >
                        <Label text="Billing Code" />
                      </FormItem>

                      <EmptyItem />
                    </GroupForm>

                    <GroupForm
                      colCount={2}
                      colSpan={2}
                      key={`Group3_${item.key}`}
                    >
                      <FormItem
                        key={`quanity_${item.key}`}
                        colSpan={1}
                        dataField={`vendors[${index}].items[${itemIndex}].quantity`}
                        isRequired={true}
                        editorType="dxNumberBox"
                        editorOptions={quantityOptions}
                      >
                        <Label text="Quantity" />
                        <RangeRule min={1} ignoreEmptyValue={false} />
                      </FormItem>

                      <FormItem
                        key={`price_${item.key}`}
                        colSpan={1}
                        dataField={`vendors[${index}].items[${itemIndex}].pricePerItem`}
                        isRequired={true}
                        editorType="dxNumberBox"
                        editorOptions={costOptions}
                      >
                        <Label text="Price per Item" />
                        <RangeRule min={0} ignoreEmptyValue={false} />
                      </FormItem>
                    </GroupForm>

                    <FormItem
                      key={`url_${item.key}`}
                      dataField={`vendors[${index}].items[${itemIndex}].url`}
                      isRequired={true}
                      colSpan={2}
                    >
                      <Label text="URL" />
                    </FormItem>

                    <FormItem
                      key={`notes_${item.key}`}
                      dataField={`vendors[${index}].items[${itemIndex}].notes`}
                      colSpan={2}
                      editorType="dxTextArea"
                    >
                      <Label text="Notes" />
                    </FormItem>
                  </Tab>
                ))}
              </TabbedItem>
            </GroupForm>
          ))}
          <FormItem
            itemType="button"
            horizontalAlignment="left"
            buttonOptions={{
              icon: "add",
              text: "Add Vendor",
              onClick: addVendor,
            }}
          />
        </Form>

        <Form>
          <GroupForm>
            <FormItem
              dataField="total"
              label={{ text: "Total Cost" }}
              editorType="dxNumberBox"
              editorOptions={{
                value: orderTotal,
                format: "$#,##0.00",
                readOnly: true,
                inputAttr: { style: "color: #c2bfbf !important" },
              }}
            />
          </GroupForm>
        </Form>
      </Popup>
    </>
  );
}

const formatDate = (date) => {
  if (!date) return "";
  const copyDate = new Date(date);
  const formattedDate = copyDate.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const formattedTime = copyDate.toLocaleDateString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${formattedDate}, ${formattedTime}`;
};
